/**
 * Time Capsule API Service
 *
 * Handles all API calls to the Time Capsule Analytics API
 */

import {
    API_BASE_URL,
    API_TIMEOUT_MS,
    API_RETRY_ATTEMPTS,
    API_RETRY_DELAY_MS,
    MAX_SELFIE_SIZE_KB,
    AgeGroupValue
} from '../utils/constants';
import { TrackingEvent } from "../utils/eventTracking";

export interface VisitorSignupData {
    name: string;
    email: string;
    ageGroup: AgeGroupValue;
    nationality: string;
    language: string;
}

export interface VisitorSignupResponse {
    visitorId: string;
    message?: string;
    // Add other response fields as needed based on actual API response
}

export interface SelfieUploadResponse {
    id: string;
    filename: string;
    message: string;
    // Add other response fields as needed
}

export interface ApiError {
    message: string;
    status: number;
    details?: any;
}

// ZonesStatus API interfaces
export interface ZoneStatusData {
    status: 'available' | 'processing' | 'error';
    claimedBy?: string;
    payload?: any; // Flexible payload structure to match API
}

export interface ZoneStatusResponse {
    zoneId: string;
    status: 'available' | 'processing' | 'error';
    payload?: any; // Can be any object structure, as seen in API response
    claimedBy?: string | null;
    updatedAt: string;
}

export interface ZonePayloadData {
    payload: any; // Flexible payload structure to match API
}

class TimeCapsuleApiService {
    /**
     * Get authentication headers for endpoints that need header-based auth
     * Based on backend notes: zones-status/update, edited-selfie upload, event/track-lidar need header auth
     */
    private getAuthHeaders(endpoint: string, visitorId?: string): Record<string, string> {
        // Endpoints that need header-based authentication
        const needsHeaderAuth = [
            '/zones-status/',
            '/visitors/selfie-edited',
            '/events/track'
        ].some(path => endpoint.includes(path));

        if (needsHeaderAuth) {
            // Get visitor ID from parameter (primary) or localStorage (fallback)
            let authVisitorId = visitorId || localStorage.getItem('visitorId') || '';
            
            if (authVisitorId) {
                console.log(`🔐 Adding auth headers for ${endpoint}:`, {
                    visitorId: `${authVisitorId.substring(0, 8)}...`,
                    headers: ['Authorization', 'X-Visitor-ID'],
                    source: visitorId ? 'parameter' : 'localStorage'
                });
                
                return {
                    'Authorization': `Bearer ${authVisitorId}`,
                    'X-Visitor-ID': authVisitorId
                };
            } else {
                console.warn(`⚠️ No visitor ID found for auth headers on ${endpoint}`);
            }
        }

        return {};
    }

    /**
     * Determine credentials strategy based on endpoint
     */
    private getCredentialsStrategy(endpoint: string): RequestCredentials {
        // Endpoints that explicitly don't need cookies (per API docs)
        const noCookieEndpoints = [
            '/visitors/selfie-edited',
            '/health'
        ];

        // Endpoints that need header auth instead of cookies
        // const headerAuthEndpoints = [
            // '/zones-status/',
        // ];

        if (noCookieEndpoints.some(path => endpoint.includes(path))) {
            return 'omit';
        }

        // Default to include credentials for other endpoints
        return 'include';
    }

    /**
     * Simple retry wrapper for API requests with exponential backoff
     */
    private async makeRequestWithRetry<T>(
        endpoint: string,
        options: RequestInit = {},
        retryCount: number = 0,
        visitorId?: string
    ): Promise<T> {
        try {
            return await this.makeRequest<T>(endpoint, options, visitorId);
        } catch (error) {
            const apiError = error as ApiError;

            // Retry on network errors or 5xx server errors
            const shouldRetry = (
                apiError.status === 0 || // Network error
                apiError.status === 408 || // Timeout
                (apiError.status >= 500 && apiError.status < 600) // Server errors
            );

            if (shouldRetry && retryCount < API_RETRY_ATTEMPTS - 1) {
                console.warn(`API request failed (attempt ${retryCount + 1}/${API_RETRY_ATTEMPTS}):`, apiError.message);

                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, API_RETRY_DELAY_MS * (retryCount + 1)));

                return this.makeRequestWithRetry<T>(endpoint, options, retryCount + 1, visitorId);
            }

            throw error;
        }
    }

    /**
     * Make API request with proper authentication strategy
     */
    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {},
        visitorId?: string
    ): Promise<T> {
        const url = `${API_BASE_URL}${endpoint}`;

        // Get authentication headers and credentials strategy
        const authHeaders = this.getAuthHeaders(endpoint, visitorId);
        const credentials = this.getCredentialsStrategy(endpoint);

        // Don't set Content-Type for FormData - let browser handle it
        const isFormData = options.body instanceof FormData;
        const defaultHeaders = isFormData ? 
            { ...authHeaders, ...options.headers } : 
            { 'Content-Type': 'application/json', ...authHeaders, ...options.headers };

        console.log(`🔄 API Request:`, {
            method: options.method || 'GET',
            url,
            credentials,
            hasAuthHeaders: Object.keys(authHeaders).length > 0,
            authHeaders: Object.keys(authHeaders),
            isFormData,
            visitorIdProvided: !!visitorId
        });

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

        try {
            const response = await fetch(url, {
                ...options,
                headers: defaultHeaders,
                signal: controller.signal,
                credentials,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch {
                    errorData = { message: errorText };
                }

                console.error(`❌ API Error (${response.status}):`, {
                    url,
                    status: response.status,
                    statusText: response.statusText,
                    error: errorData,
                    credentials,
                    hadAuthHeaders: Object.keys(authHeaders).length > 0,
                    visitorIdProvided: !!visitorId
                });

                throw {
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    status: response.status,
                    details: errorData
                } as ApiError;
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                console.log(`✅ API Success:`, { url, data });
                return data;
            } else {
                const data = await response.text() as any;
                console.log(`✅ API Success (text):`, { url, data });
                return data;
            }
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof DOMException && error.name === 'AbortError') {
                throw {
                    message: `Request timeout after ${API_TIMEOUT_MS / 1000} seconds`,
                    status: 408,
                    details: error
                } as ApiError;
            }

            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw {
                    message: 'Network error - please check your internet connection',
                    status: 0,
                    details: error
                } as ApiError;
            }
            throw error;
        }
    }

    /**
     * Sign up a new visitor
     */
    async signupVisitor(data: VisitorSignupData): Promise<VisitorSignupResponse> {
        console.log('🚀 Starting visitor signup process:', {
            endpoint: '/visitors/signup',
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
            isAndroid: /Android/.test(navigator.userAgent),
            data: {
                name: data.name,
                email: data.email,
                ageGroup: data.ageGroup,
                nationality: data.nationality,
                language: data.language
            }
        });

        try {
            const result = await this.makeRequestWithRetry<VisitorSignupResponse>('/visitors/signup', {
                method: 'POST',
                body: JSON.stringify(data),
            });

            console.log('✅ Visitor signup successful:', {
                visitorId: result.visitorId,
                message: result.message
            });

            return result;
        } catch (error) {
            console.error('❌ Visitor signup failed:', {
                error,
                userAgent: navigator.userAgent,
                isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
                isAndroid: /Android/.test(navigator.userAgent)
            });
            throw error;
        }
    }

    /**
     * Get visitor details by ID
     * Note: This endpoint requires cookie-based authentication where the visitor ID in the cookie
     * must match the visitor ID in the URL parameter
     */
    async getVisitor(id: string): Promise<any> {
        console.log(`🔍 Getting visitor data for ID: ${id}`);
        
        // Check if we have a visitor ID cookie that matches
        const cookies = document.cookie.split(';').reduce((acc, cookie) => {
            const [key, value] = cookie.trim().split('=');
            acc[key] = value;
            return acc;
        }, {} as Record<string, string>);
        
        const cookieVisitorId = cookies['v'];
        console.log('🍪 Cookie analysis:', {
            requestedId: id,
            cookieVisitorId: cookieVisitorId,
            match: cookieVisitorId === id,
            allCookies: Object.keys(cookies)
        });
        
        if (cookieVisitorId && cookieVisitorId !== id) {
            console.warn('⚠️ Visitor ID mismatch detected:', {
                reduxVisitorId: id,
                cookieVisitorId: cookieVisitorId,
                recommendation: 'Using cookie visitor ID for API call'
            });
            
            // Use the cookie visitor ID instead of the Redux one for the API call
            // This handles the case where Redux has a stale visitor ID
            return this.makeRequest<any>(`/visitors/${cookieVisitorId}`, {
                method: 'GET',
            });
        }
        
        return this.makeRequest<any>(`/visitors/${id}`, {
            method: 'GET',
        });
    }

    /**
     * Upload visitor selfie
     */
    async uploadSelfie(
        visitorId: string,
        selfieBlob: Blob,
        filename: string = 'selfie.jpg'
    ): Promise<SelfieUploadResponse> {
        // Validate file size
        const fileSizeKB = selfieBlob.size / 1024;
        if (fileSizeKB > MAX_SELFIE_SIZE_KB) {
            throw {
                message: `File size (${Math.round(fileSizeKB)}KB) exceeds maximum allowed size (${MAX_SELFIE_SIZE_KB}KB)`,
                status: 413,
                details: {fileSizeKB, maxSizeKB: MAX_SELFIE_SIZE_KB}
            } as ApiError;
        }

        const formData = new FormData();
        formData.append('photo', selfieBlob, filename);
        // Note: /visitors/selfie endpoint doesn't expect visitorId field

        console.log('📤 Uploading selfie with FormData:', {
            visitorId,
            visitorIdType: typeof visitorId,
            visitorIdLength: visitorId?.length,
            filename,
            fileSizeKB: Math.round(fileSizeKB),
            blobType: selfieBlob.type
        });

        // Debug FormData contents
        console.log('📤 FormData entries:');
        console.log(`  visitorId: ${formData.get('visitorId')}`);
        console.log(`  selfie: [File] ${(formData.get('selfie') as File)?.size || 'unknown'} bytes`);

        // Use makeRequest method which now handles FormData properly
        return await this.makeRequest<SelfieUploadResponse>('/visitors/selfie', {
            method: 'POST',
            body: formData,
        });
    }

    /**
     * Upload edited selfie (no cookie required)
     */
    async uploadEditedSelfie(
        visitorId: string,
        selfieBlob: Blob,
        filename: string = 'edited-selfie.jpg'
    ): Promise<SelfieUploadResponse> {
        const formData = new FormData();
        formData.append('visitorId', visitorId);
        formData.append('editedSelfie', selfieBlob, filename);

        return this.makeRequest<SelfieUploadResponse>('/visitors/selfie-edited', {
            method: 'POST',
            body: formData,
        }, visitorId);
    }

    /**
     * Track an event
     */
    async trackEvent(eventType: string, {userMetadata, zone, metadata, timestamp}: TrackingEvent, visitorId?: string): Promise<any> {

        const testTimestamp = timestamp ? {
            _testTimestamp: timestamp
        } : {}

        return this.makeRequest<any>('/events/track', {
            method: 'POST',
            body: JSON.stringify({
                eventType,
                zone,
                metadata: {
                    eventType,
                    ...userMetadata,
                    ...metadata as Object,
                },
                ...testTimestamp,
            }),
        }, visitorId);
    }

    /**
     * Get analytics data
     */
    async getAnalytics(filters?: any): Promise<any> {
        const queryParams = filters ? '?' + new URLSearchParams(filters).toString() : '';
        return this.makeRequest<any>(`/events/analytics${queryParams}`, {
            method: 'GET',
        });
    }

    /**
     * Update zone status
     */
    async updateZoneStatus(zoneId: string, statusData: ZoneStatusData, visitorId?: string): Promise<ZoneStatusResponse> {
        return this.makeRequest<ZoneStatusResponse>(`/zones-status/${zoneId}`, {
            method: 'PUT',
            body: JSON.stringify(statusData),
        }, visitorId);
    }

    /**
     * Get zone status with enhanced client-side session mapping
     * Since API doesn't use our visitorId parameter, we need client-side mapping
     */
    async getZoneStatus(zoneId: string, visitorId?: string): Promise<ZoneStatusResponse> {
        const zoneStatus = await this.makeRequest<ZoneStatusResponse>(`/zones-status/${zoneId}`, {
            method: 'GET',
        }, visitorId);

        console.log(`🔍 Raw zone status from API for ${zoneId}:`, {
            status: zoneStatus.status,
            claimedBy: zoneStatus.claimedBy,
            requestingVisitorId: visitorId,
            directMatch: zoneStatus.claimedBy === visitorId
        });

        // Enhanced client-side session mapping (async to prevent UI blocking)
        if (visitorId && zoneStatus.claimedBy && zoneStatus.claimedBy !== visitorId) {
            // Use Promise to make localStorage access non-blocking
            const mappedVisitorId = await new Promise<string | null>((resolve) => {
                setTimeout(() => {
                    resolve(localStorage.getItem(`zoneClaimMapping_${zoneId}`));
                }, 0);
            });
            
            if (mappedVisitorId === visitorId) {
                console.log(`🔄 Applying client-side session mapping for ${zoneId}:`, {
                    apiClaimedBy: zoneStatus.claimedBy,
                    mappingToVisitorId: visitorId,
                    reason: 'API uses its own visitor ID system, correcting for UI consistency'
                });

                // Return modified zone status with correct visitor ID for client-side consistency
                return {
                    ...zoneStatus,
                    claimedBy: visitorId,
                    // Add a flag to indicate this is client-side mapped
                    _clientSideMapped: true,
                    _originalClaimedBy: zoneStatus.claimedBy
                } as ZoneStatusResponse & { _clientSideMapped?: boolean; _originalClaimedBy?: string };
            } else {
                console.log(`ℹ️ No client-side mapping found for ${zoneId}:`, {
                    apiClaimedBy: zoneStatus.claimedBy,
                    requestingVisitorId: visitorId,
                    storedMapping: mappedVisitorId,
                    result: 'Using API data as-is'
                });
            }
        }

        return zoneStatus;
    }

    /**
     * Claim zone status to 'processing'
     * Enhanced with robust client-side mapping since API ignores visitorId parameter
     */
    async claimZoneStatus(zoneId: string, visitorId?: string): Promise<ZoneStatusResponse> {
        console.log(`🎯 Claiming zone status for: ${zoneId}`, { 
            hasVisitorId: !!visitorId,
            visitorIdPreview: visitorId ? `${visitorId.substring(0, 8)}...` : 'none'
        });

        // Step 1: Make the actual claim API call
        await this.makeRequest<any>(`/zones-status/${zoneId}/claim`, { 
            method: 'POST' 
        });

        console.log(`✅ Zone claim successful for ${zoneId}`);

        // Step 2: Store client-side mapping for UI consistency (non-blocking)
        if (visitorId) {
            localStorage.setItem(`zoneClaimMapping_${zoneId}`, visitorId);
            console.log(`💾 Stored client-side mapping for ${zoneId}:`, {
                visitorId: `${visitorId.substring(0, 8)}...`,
                storageKey: `zoneClaimMapping_${zoneId}`,
                purpose: 'UI consistency since API uses different visitor ID system'
            });
        }

        // Step 3: Get the actual zone status immediately (this is what Redux expects)
        const zoneStatus = await this.getZoneStatus(zoneId, visitorId);
        
        console.log(`🔍 Final zone status for ${zoneId}:`, {
            status: zoneStatus.status,
            claimedBy: zoneStatus.claimedBy,
            visitorId,
            isMatch: zoneStatus.claimedBy === visitorId,
            hasMappingFlag: !!(zoneStatus as any)._clientSideMapped,
            originalClaimedBy: (zoneStatus as any)._originalClaimedBy,
            result: zoneStatus.claimedBy === visitorId ?
                '✅ SUCCESS - Client-side mapping working' :
                '❌ FAILED - Mapping issue detected'
        });

        // Step 4: Do additional async logging without blocking (optional)
        setTimeout(async () => {
            try {
                console.log(`📊 Post-claim verification for ${zoneId} completed`);
            } catch (error) {
                console.error(`❌ Post-claim verification failed for ${zoneId}:`, error);
            }
        }, 0);

        return zoneStatus;
    }

    /**
     * Update zone status payload
     * Used by visitor/mobile client to update payload when zone is claimed
     */
    async updateZonePayload(zoneId: string, payloadData: ZonePayloadData, visitorId?: string): Promise<ZoneStatusResponse> {
        console.log(`🔄 Updating zone payload for ${zoneId}:`, {
            endpoint: `/zones-status/${zoneId}/payload`,
            method: 'PUT',
            payload: payloadData,
            option: payloadData.payload?.option,
            visitorId: visitorId ? `${visitorId.substring(0, 8)}...` : 'none',
            note: 'Using header-based authentication'
        });

        return this.makeRequest<ZoneStatusResponse>(`/zones-status/${zoneId}/payload`, {
            method: 'PUT',
            body: JSON.stringify(payloadData),
        }, visitorId);
    }

    /**
     * Health check
     */
    async healthCheck(): Promise<any> {
        return this.makeRequest<any>('/health', {
            method: 'GET',
        });
    }
}

// Export singleton instance
export const timeCapsuleApi = new TimeCapsuleApiService();

// Utility function to convert data URL to Blob
export function dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], {type: mime});
}